<template>
    <div class="w-full bg-white p-6 rounded-xl shadow-sm">

        <div class="flex flex-wrap gap-4 mb-6">
            <div class="flex flex-col">
                <label class="text-sm font-medium mb-1 text-gray-600">{{ $t('orgDashboard.reports.xAxis') }}</label>
                <select v-model="xAxis"
                    class="border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200">
                    <option v-for="param in parameters" :key="`x-${param}`" :value="param">{{ param }}</option>
                </select>
            </div>

            <div class="flex flex-col">
                <label class="text-sm font-medium mb-1 text-gray-600">{{ $t('orgDashboard.reports.yAxis') }}</label>
                <select v-model="yAxis"
                    class="border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200">
                    <option v-for="param in parameters" :key="`y-${param}`" :value="param">{{ param }}</option>
                </select>
            </div>

            <div class="flex flex-col">
                <label class="text-sm font-medium mb-1 text-gray-600">{{ $t('orgDashboard.reports.bubbleSize') }}</label>
                <select v-model="bubbleSize"
                    class="border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200">
                    <option v-for="param in parameters" :key="`size-${param}`" :value="param">{{ param }}</option>
                </select>
            </div>
        </div>

        <!-- Everything wrapped in a position-relative div to allow absolute positioning of tooltips -->
        <div class="relative">
            <!-- The chart container with light background and rounded corners -->
            <div class="w-full h-96 bg-gray-50 rounded-xl mb-4" ref="chartContainer">
                <!-- Loading message -->
                <div v-if="loading"
                    class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
                    <div class="flex items-center space-x-2">
                        <svg class="animate-spin h-5 w-5 text-purple-500" xmlns="http://www.w3.org/2000/svg" fill="none"
                            viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4">
                            </circle>
                            <path class="opacity-75" fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                            </path>
                        </svg>
                        <span class="font-medium text-gray-700">{{ $t('orgDashboard.reports.loadingChart') }}</span>
                    </div>
                </div>

                <!-- SVG container with viewBox for responsive scaling -->
                <svg ref="svg" width="100%" height="100%" preserveAspectRatio="xMidYMid meet"
                    :viewBox="`0 0 ${totalWidth} ${totalHeight}`" style="width: 100%; min-width: 100%;">
                    <!-- Background -->
                    <rect x="0" y="0" :width="totalWidth" :height="totalHeight" fill="#f9fafb" rx="8" ry="8" />

                    <!-- Main chart group with margin -->
                    <g :transform="`translate(${margin.left},${margin.top})`">
                        <!-- Quadrant background colors (matching website palette with lower opacity) -->
                        <rect :x="0" :y="0" :width="chartWidth / 2" :height="chartHeight / 2" fill="#6366f1"
                            opacity="0.05" rx="8" ry="8" /> <!-- Top-left -->
                        <rect :x="chartWidth / 2" :y="0" :width="chartWidth / 2" :height="chartHeight / 2"
                            fill="#10b981" opacity="0.05" rx="8" ry="8" /> <!-- Top-right -->
                        <rect :x="0" :y="chartHeight / 2" :width="chartWidth / 2" :height="chartHeight / 2"
                            fill="#f97316" opacity="0.05" rx="8" ry="8" /> <!-- Bottom-left -->
                        <rect :x="chartWidth / 2" :y="chartHeight / 2" :width="chartWidth / 2" :height="chartHeight / 2"
                            fill="#8b5cf6" opacity="0.05" rx="8" ry="8" /> <!-- Bottom-right -->

                        <!-- Grid lines -->
                        <line v-for="tick in 5" :key="`grid-x-${tick}`" :x1="0" :y1="chartHeight * tick / 5"
                            :x2="chartWidth" :y2="chartHeight * tick / 5" stroke="#e5e7eb" stroke-width="1" />
                        <line v-for="tick in 5" :key="`grid-y-${tick}`" :x1="chartWidth * tick / 5" :y1="0"
                            :x2="chartWidth * tick / 5" :y2="chartHeight" stroke="#e5e7eb" stroke-width="1" />

                        <!-- Quadrant lines -->
                        <line :x1="chartWidth / 2" :y1="0" :x2="chartWidth / 2" :y2="chartHeight" stroke="#d1d5db"
                            stroke-width="1" stroke-dasharray="4,4" />
                        <line :x1="0" :y1="chartHeight / 2" :x2="chartWidth" :y2="chartHeight / 2" stroke="#d1d5db"
                            stroke-width="1" stroke-dasharray="4,4" />

                        <!-- X and Y axes -->
                        <line :x1="0" :y1="chartHeight" :x2="chartWidth" :y2="chartHeight" stroke="#9ca3af"
                            stroke-width="1.5" />
                        <line :x1="0" :y1="0" :x2="0" :y2="chartHeight" stroke="#9ca3af" stroke-width="1.5" />

                        <!-- X-axis ticks and labels -->
                        <template v-for="(tick, i) in xTicks">
                            <line :key="`x-tick-${i}`" :x1="xScale(tick)" :y1="chartHeight" :x2="xScale(tick)"
                                :y2="chartHeight + 5" stroke="#9ca3af" stroke-width="1" />
                            <text :key="`x-label-${i}`" :x="xScale(tick)" :y="chartHeight + 20" text-anchor="middle"
                                font-size="12" fill="#6b7280">
                                {{ Math.round(tick) }}
                            </text>
                        </template>

                        <!-- Y-axis ticks and labels -->
                        <template v-for="(tick, i) in yTicks">
                            <line :key="`y-tick-${i}`" :x1="-5" :y1="yScale(tick)" :x2="0" :y2="yScale(tick)"
                                stroke="#9ca3af" stroke-width="1" />
                            <text :key="`y-label-${i}`" :x="-10" :y="yScale(tick)" text-anchor="end"
                                dominant-baseline="middle" font-size="12" fill="#6b7280">
                                {{ Math.round(tick) }}
                            </text>
                        </template>

                        <!-- Axis titles -->
                        <text :x="chartWidth / 2" :y="chartHeight + 45" text-anchor="middle" font-size="14"
                            font-weight="500" fill="#4b5563">{{
                                xAxis }}</text>
                        <text :transform="`translate(-45,${chartHeight / 2}) rotate(-90)`" text-anchor="middle"
                            font-size="14" font-weight="500" fill="#4b5563">{{ yAxis }}</text>

                        <!-- Quadrant labels with softer styling -->
                        <text :x="chartWidth * 0.25" :y="chartHeight * 0.25" text-anchor="middle"
                            dominant-baseline="middle" font-size="12" fill="#6b7280" font-weight="500">
                            {{ $t('orgDashboard.reports.low') }} {{ xAxis }}, {{ $t('orgDashboard.reports.high') }} {{ yAxis }}
                        </text>
                        <text :x="chartWidth * 0.75" :y="chartHeight * 0.25" text-anchor="middle"
                            dominant-baseline="middle" font-size="12" fill="#6b7280" font-weight="500">
                            {{ $t('orgDashboard.reports.high') }} {{ xAxis }}, {{ $t('orgDashboard.reports.high') }} {{ yAxis }}
                        </text>
                        <text :x="chartWidth * 0.25" :y="chartHeight * 0.75" text-anchor="middle"
                            dominant-baseline="middle" font-size="12" fill="#6b7280" font-weight="500">
                            {{ $t('orgDashboard.reports.low') }} {{ xAxis }}, {{ $t('orgDashboard.reports.low') }} {{ yAxis }}
                        </text>
                        <text :x="chartWidth * 0.75" :y="chartHeight * 0.75" text-anchor="middle"
                            dominant-baseline="middle" font-size="12" fill="#6b7280" font-weight="500">
                            {{ $t('orgDashboard.reports.high') }} {{ xAxis }}, {{ $t('orgDashboard.reports.low') }} {{ yAxis }}
                        </text>

                        <!-- Data points (bubbles) with colorful avatars matching your UI theme -->
                        <g v-for="(point, i) in transformedData" :key="`point-${i}`"
                            @mouseenter="showTooltip(point, $event)" @mousemove="updateTooltipPosition($event)"
                            @mouseleave="hideTooltip">
                            <circle :cx="xScale(point.x)" :cy="yScale(point.y)" :r="zScale(point.z)"
                                :fill="getBubbleColor(point.userId)" opacity="0.85" stroke="#ffffff" stroke-width="2" />
                            <!-- Employee initials inside bubbles -->
                            <text v-if="point.name && zScale(point.z) > 12" :x="xScale(point.x)" :y="yScale(point.y)"
                                text-anchor="middle" dominant-baseline="middle" font-size="10" fill="white"
                                font-weight="bold" pointer-events="none">
                                {{ getInitials(point.name) }}
                            </text>
                        </g>
                    </g>
                </svg>
            </div>

            <!-- Simple tooltip implementation -->
            <div v-show="tooltipVisible" ref="tooltip"
                class="absolute bg-white p-3 rounded-lg border border-gray-100 shadow-lg text-sm z-50 pointer-events-none"
                style="transform: translate(-50%, -100%); margin-top: -10px;">
                <div v-if="tooltipData">
                    <div class="font-bold mb-2 text-gray-800">{{ tooltipData.name }}</div>
                    <div v-for="(value, key) in tooltipData.metrics" :key="key" class="mb-1 flex justify-between">
                        <span class="font-medium text-gray-600">{{ key }}:</span>
                        <span class="ml-2 text-gray-800">{{ value }}</span>
                    </div>
                </div>
            </div>

            <div class="mt-4 p-5 bg-gray-50 rounded-xl border border-gray-100">
                <h3 class="font-medium text-lg md:text-xl mb-4 text-gray-800 flex items-center">
                    <div class="flex-shrink-0 mr-4">
                        <div class="w-10 h-10 bg-primary flex items-center justify-center rounded-full text-white">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" viewBox="0 0 20 20"
                                fill="currentColor">
                                <path fill-rule="evenodd"
                                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                    clip-rule="evenodd" />
                            </svg>
                        </div>
                    </div>
                    {{ $t('orgDashboard.reports.howToReadChart') }}
                </h3>

                <div class="grid grid-cols-1 lg:grid-cols-2">
                    <div class="bg-white p-4 md:p-5 rounded-lg border border-gray-100 shadow-sm">
                        <div class="font-medium text-base mb-3 text-primary">{{ $t('orgDashboard.reports.positioning') }}</div>
                        <div class="flex items-center mb-3">
                            <div class="w-4 h-4 bg-primary rounded-full mr-3"></div>
                            <div class="text-base text-gray-700">{{ $t('orgDashboard.reports.horizontalPosition') }} <span class="font-medium">{{
                                xAxis
                                    }}</span> score</div>
                        </div>
                        <div class="flex items-center">
                            <div class="w-4 h-4 bg-primary rounded-full mr-3"></div>
                            <div class="text-base text-gray-700">{{ $t('orgDashboard.reports.verticalPosition') }} <span class="font-medium">{{
                                yAxis
                                    }}</span> score</div>
                        </div>
                    </div>

                    <div class="bg-white p-4 md:p-5 rounded-lg border border-gray-100 shadow-sm">
                        <div class="font-medium text-base mb-3 text-primary">{{ $t('orgDashboard.reports.bubbleRepresentation') }}</div>
                        <div class="flex items-center mb-3">
                            <div
                                class="w-8 h-8 bg-gradient-to-r from-primary to-primary rounded-full mr-3 flex items-center justify-center text-white text-sm font-bold">
                                AB</div>
                            <div class="text-base text-gray-700">{{ $t('orgDashboard.reports.eachBubbleRepresents') }}</div>
                        </div>
                        <div class="flex items-center">
                            <div class="flex mr-3">
                                <div class="w-8 h-8 bg-primary rounded-full"></div>
                            </div>
                            <div class="text-base text-gray-700">{{ $t('orgDashboard.reports.sizeIndicates') }} <span class="font-medium">{{
                                bubbleSize }}</span>
                                score</div>
                        </div>
                    </div>

                    <div class="bg-white p-4 md:p-5 rounded-lg border border-gray-100 shadow-sm">
                        <div class="font-medium text-base mb-3 text-primary">{{ $t('orgDashboard.reports.quadrants') }}</div>
                        <div class="grid grid-cols-2 gap-3 text-sm">
                            <div class="bg-blue-50 p-3 rounded">
                                <div class="font-medium">{{ $t('orgDashboard.reports.topLeft') }}</div>
                                <div class="text-gray-600">{{ $t('orgDashboard.reports.low') }} {{ xAxis }}, {{ $t('orgDashboard.reports.high') }} {{ yAxis }}</div>
                            </div>
                            <div class="bg-green-50 p-3 rounded">
                                <div class="font-medium">{{ $t('orgDashboard.reports.topRight') }}</div>
                                <div class="text-gray-600">{{ $t('orgDashboard.reports.high') }} {{ xAxis }}, {{ $t('orgDashboard.reports.high') }} {{ yAxis }}</div>
                            </div>
                            <div class="bg-orange-50 p-3 rounded">
                                <div class="font-medium">{{ $t('orgDashboard.reports.bottomLeft') }}</div>
                                <div class="text-gray-600">{{ $t('orgDashboard.reports.low') }} {{ xAxis }}, {{ $t('orgDashboard.reports.low') }} {{ yAxis }}</div>
                            </div>
                            <div class="bg-purple-50 p-3 rounded">
                                <div class="font-medium">{{ $t('orgDashboard.reports.bottomRight') }}</div>
                                <div class="text-gray-600">{{ $t('orgDashboard.reports.high') }} {{ xAxis }}, {{ $t('orgDashboard.reports.low') }} {{ yAxis }}</div>
                            </div>
                        </div>
                    </div>

                    <div class="bg-white p-4 md:p-5 rounded-lg border border-gray-100 shadow-sm">
                        <div class="font-medium text-base mb-3 text-primary">{{ $t('orgDashboard.reports.interaction') }}</div>
                        <div class="flex mb-3">
                            <div
                                class="flex-shrink-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center mr-3">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-white" viewBox="0 0 20 20"
                                    fill="currentColor">
                                    <path fill-rule="evenodd"
                                        d="M6.672 1.911a1 1 0 10-1.932.518l.259.966a1 1 0 001.932-.518l-.26-.966zM2.429 4.74a1 1 0 10-.517 1.932l.966.259a1 1 0 00.517-1.932l-.966-.26zm8.814-.569a1 1 0 00-1.415-1.414l-.707.707a1 1 0 101.415 1.415l.707-.708zm-7.071 7.072l.707-.707A1 1 0 003.465 9.12l-.708.707a1 1 0 001.415 1.415zm3.2-5.171a1 1 0 00-1.3 1.3l4 10a1 1 0 001.823.075l1.38-2.759 3.018 3.02a1 1 0 001.414-1.415l-3.019-3.02 2.76-1.379a1 1 0 00-.076-1.822l-10-4z"
                                        clip-rule="evenodd" />
                                </svg>
                            </div>
                            <div>
                                <div class="text-base text-gray-700 font-medium">{{ $t('orgDashboard.reports.hoverOverBubble') }}</div>
                                <div class="text-sm text-gray-500">{{ $t('orgDashboard.reports.revealsDetailedMetrics') }}</div>
                            </div>
                        </div>
                        <div class="flex">
                            <div class="w-8 h-8 bg-gradient-to-r from-purple-300 to-pink-300 rounded-full mr-3"></div>
                            <div>
                                <div class="text-base text-gray-700 font-medium">{{ $t('orgDashboard.reports.colorCoding') }}</div>
                                <div class="text-sm text-gray-500">{{ $t('orgDashboard.reports.differentColorsHelp') }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import constants from "@/constants";
import { kvdrOrgConfig } from "@/assets/data/kvdrOrgConfigs.js";

export default {
    name: 'KVDRPlot',

    data() {
        return {
            kvdrConfig: {},
            assessmentData: [],
            parameters: [
                'Fitment (Fit)',
                'Performance (Drive)',
                'Succession (Secure)',
                'Appraisal (Self)'
            ],
            xAxis: 'Fitment (Fit)',
            yAxis: 'Performance (Drive)',
            bubbleSize: 'Appraisal (Self)',
            margin: { top: 40, right: 40, bottom: 70, left: 60 },
            tooltipVisible: false,
            tooltipX: 0,
            tooltipY: 0,
            tooltipData: null,
            loading: true,
            totalWidth: 800,
            totalHeight: 600,
            chartWidth: 800 - 60 - 40,
            chartHeight: 600 - 40 - 70,
            xTicks: [],
            yTicks: [],
            // ... (other data props)
        };
    },
    computed: {
        chartData() {
            if (this.assessmentData && this.assessmentData.length > 0) {
                return this.assessmentData.map((data) => {
                    return {
                        "name": data.name,
                        "avatar": data.avatar,
                        "userId": data.userId,
                        'Fitment (Fit)': data['Fitment (Fit)'] || 0,
                        'Performance (Drive)': data['Performance (Drive)'] || 0,
                        'Succession (Secure)': data['Succession (Secure)'] || 0,
                        'Appraisal (Self)': data['Appraisal'] || 0
                    };
                });
            }
            return [];
        },
        transformedData() {
            return this.chartData.map(item => ({
                ...item,
                x: item[this.xAxis],
                y: item[this.yAxis],
                z: item[this.bubbleSize] / 5,
            }));
        },
        userId() {
            return this.$store.state.auth.user._id;
        },
        orgData() {
            return this.$store.state.organization.orgData;
        },
        kvdrData() {
            return this.$store.state.kvdr.selectedKVDR;
        },
        employeesData() {
            return this.$store.state.organization.employees;
        },
    },
    watch: {
        xAxis() {
            this.updateChart();
        },
        yAxis() {
            this.updateChart();
        },
        bubbleSize() {
            this.updateChart();
        },
        transformedData() {
            this.updateChart();
        },
        kvdrData(val) {
            if (val) {
                this.extractKvdrRoleData();
            }
        },
    },
    mounted() {
        if (this.kvdrData) {
            this.extractKvdrRoleData()
        }
        this.$nextTick(() => {
            this.updateDimensions();
            this.updateChart();
            window.addEventListener('resize', this.handleResize);
            this.loading = false;
        });
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        async fetchOrgConfig() {
            let finalConfig = {};
            try {
                const orgId = this.orgData._id;
                const token = localStorage.getItem('feathers-jwt') || localStorage.getItem('accessToken');
                const baseUrl = process.env.VUE_APP_API_URL || 'https://api.getboarded.tech';
                
                const response = await fetch(`${baseUrl}/kvdr-org-config?orgId=${orgId}`, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
                });
                const json = await response.json();
                
                if (json.data && json.data.length > 0) {
                    finalConfig = json.data[0];
                }
            } catch (err) {
                console.error("Error fetching config:", err);
            }

            // LocalStorage Override (If API gives default or fails)
            if (!finalConfig.orgId || finalConfig.orgId === "000000000000000000000000" || finalConfig.orgId === "0") {
                try {
                    const slug = this.orgData.slug;
                    if (slug) {
                        const localDataStr = localStorage.getItem(`onboardingConfigs_${slug}`);
                        if (localDataStr) {
                            const localData = JSON.parse(localDataStr);
                            if (localData.appraisal) finalConfig.appraisalData = localData.appraisal;
                            if (localData.fitment) finalConfig.fitmentData = localData.fitment;
                            if (localData.succession) finalConfig.successionData = localData.succession;
                            if (localData.performance) finalConfig.performanceData = localData.performance;
                        }
                    }
                } catch (e) {
                    console.warn("Could not load local dynamic config", e);
                }
            }

            // Fallback mappings
            if (!finalConfig.appraisalData && finalConfig.appraisal) finalConfig.appraisalData = finalConfig.appraisal;
            if (!finalConfig.fitmentData && finalConfig.fitment) finalConfig.fitmentData = finalConfig.fitment;

            if (Object.keys(finalConfig).length === 0) {
                 finalConfig = kvdrOrgConfig(this.orgData._id);
            }

            this.kvdrConfig = finalConfig;
        },
        handleResize() {
            this.updateDimensions();
            this.updateChart();
        },
        getUserById(userId) {
            return this.employeesData.filter((user) => user.userId == userId);
        },
        // Helper to calculate total score for a specific metric array
        calculateMetricTotal(userMetricData, configMetricObj) {
            if (!configMetricObj) return 0;
            
            let total = 0;
            // Iterate through Categories in Config (e.g., Knowledge, Behaviour)
            for (const category in configMetricObj) {
                const attributes = configMetricObj[category];
                if (Array.isArray(attributes)) {
                    attributes.forEach(attr => {
                        const compositeKey = `${category}|${attr.key}`;
                        const weight = parseFloat(attr.value) || 0;
                        
                        // Find match in user data (check composite key first, then simple key)
                        const match = (userMetricData || []).find(d => 
                            d.key === compositeKey || d.key === attr.key
                        );
                        
                        const score = match ? parseFloat(match.score !== undefined ? match.score : match.value) : 0;
                        
                        // Weighted calculation: Grade * Weight
                        total += (isNaN(score) ? 0 : score) * weight;
                    });
                }
            }
            // Return rounded total to match Org Chart modal
            return Math.round(total);
        },
        async extractKvdrRoleData() {
            let allProfiles = [];
            
            if (!this.kvdrData || !this.kvdrData.kvdrRoles || !this.orgData) {
                this.assessmentData = [];
                return;
            }

            // --- NEW: Fetch and use the dynamic config ---
            await this.fetchOrgConfig();
            const config = this.kvdrConfig;

            this.kvdrData.kvdrRoles.forEach(role => {
                if (role.kvdrRoleProfiles && role.kvdrRoleProfiles.length > 0) {
                    const profilesWithContext = role.kvdrRoleProfiles.map(profile => {
                        const userDataList = this.getUserById(profile.userId);
                        const user = userDataList && userDataList.length > 0 ? userDataList[0] : null;

                        // Identify data arrays
                        const fitData = profile.fitmentData || profile.fitment_data || [];
                        const perfData = profile.performanceData || profile.performance_data || [];
                        const succData = profile.successionData || profile.succession_data || [];
                        const appData = profile.appraisalData || profile.appraisal_data || [];

                        return {
                            ...profile,
                            roleTitle: role.title,
                            userId: user ? user.userId : profile.userId,
                            name: user ? user.fullName : "Unknown User",
                            avatar: user && user.profilePictureUrl
                                ? `${constants.s3Bucket}/editor/profile-pictures/${user.profilePictureUrl}`
                                : `https://hostedboringavatars.vercel.app/api/beam?name=${user ? user.firstName : 'Unknown'}`,
                            
                            // BUG FIX: Pass the specific config block to the weighted calculator
                            "Fitment (Fit)": this.calculateMetricTotal(fitData, config.fitmentData || config.fitment),
                            "Performance (Drive)": this.calculateMetricTotal(perfData, config.performanceData || config.performance),
                            "Succession (Secure)": this.calculateMetricTotal(succData, config.successionData || config.succession),
                            "Appraisal": this.calculateMetricTotal(appData, config.appraisalData || config.appraisal),
                        };
                    });

                    allProfiles = [...allProfiles, ...profilesWithContext];
                }
            });

            this.assessmentData = allProfiles;
            return allProfiles;
        },
        
        updateDimensions() {
            if (this.$refs.chartContainer) {
                const containerWidth = this.$refs.chartContainer.clientWidth;
                this.totalWidth = Math.max(800, containerWidth);
                this.totalHeight = 600;
                this.chartWidth = this.totalWidth - this.margin.left - this.margin.right;
                this.chartHeight = this.totalHeight - this.margin.top - this.margin.bottom;
            }
        },

        updateChart() {
            // Calculate data ranges
            if (!this.transformedData.length) return;

            const xValues = this.transformedData.map(d => d.x);
            const yValues = this.transformedData.map(d => d.y);
            const zValues = this.transformedData.map(d => d.z);

            this.xMin = Math.min(...xValues);
            this.xMax = Math.max(...xValues);
            this.yMin = Math.min(...yValues);
            this.yMax = Math.max(...yValues);
            this.zMin = Math.min(...zValues);
            this.zMax = Math.max(...zValues);

            // Add some padding to the ranges
            const xPadding = (this.xMax - this.xMin) * 0.1 || 1;
            const yPadding = (this.yMax - this.yMin) * 0.1 || 1;

            this.xMin = Math.max(0, this.xMin - xPadding);
            this.xMax = this.xMax + xPadding;
            this.yMin = Math.max(0, this.yMin - yPadding);
            this.yMax = this.yMax + yPadding;

            // Generate tick values
            this.xTicks = this.generateTicks(this.xMin, this.xMax, 5);
            this.yTicks = this.generateTicks(this.yMin, this.yMax, 5);
        },

        // Scale functions
        xScale(value) {
            if (this.xMax === this.xMin) return this.chartWidth / 2;
            return this.chartWidth * (value - this.xMin) / (this.xMax - this.xMin);
        },

        yScale(value) {
            if (this.yMax === this.yMin) return this.chartHeight / 2;
            return this.chartHeight - (this.chartHeight * (value - this.yMin) / (this.yMax - this.yMin));
        },

        zScale(value) {
            // Scale between 8 and 25 for better visibility
            const minRadius = 8;
            const maxRadius = 25;
            if (this.zMax === this.zMin) return (maxRadius + minRadius) / 2;
            return minRadius + ((value - this.zMin) / (this.zMax - this.zMin)) * (maxRadius - minRadius);
        },
        
        getBubbleColor(userId) {
            // Default color if no userId is provided
            if (!userId) return '#1E88E5'; // Default blue

            // Create a consistent hash from the userId
            let hash = 0;
            const idStr = userId.toString();
            for (let i = 0; i < idStr.length; i++) {
                hash = idStr.charCodeAt(i) + ((hash << 5) - hash);
            }

            const hues = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330];
            const baseHue = hues[Math.abs(hash) % hues.length];
            const hueShift = (Math.abs((hash << 4)) % 30) - 15;
            const finalHue = (baseHue + hueShift + 360) % 360;
            const saturation = 65 + (Math.abs((hash << 2)) % 25);
            const lightness = 25 + (Math.abs((hash >> 3)) % 30); 

            return `hsl(${finalHue}, ${saturation}%, ${lightness}%)`;
        },

        generateTicks(min, max, count) {
            const step = (max - min) / count;
            const ticks = [];
            for (let i = 0; i <= count; i++) {
                ticks.push(min + step * i);
            }
            return ticks;
        },

        getInitials(name) {
            if (!name) return '';
            return name.split(' ')
                .map(word => word.charAt(0))
                .join('')
                .substring(0, 2)
                .toUpperCase();
        },

        showTooltip(data, event) {
            const metrics = {};
            this.parameters.forEach(param => {
                metrics[param] = data[param];
            });

            this.tooltipData = {
                name: data.name,
                metrics
            };

            this.updateTooltipPosition(event);
            this.tooltipVisible = true;
        },

        updateTooltipPosition(event) {
            if (!this.$refs.tooltip) return;

            const chartRect = this.$refs.chartContainer.getBoundingClientRect();
            const x = event.clientX - chartRect.left;
            const y = event.clientY - chartRect.top;

            this.$refs.tooltip.style.left = `${x}px`;
            this.$refs.tooltip.style.top = `${y}px`;
        },

        hideTooltip() {
            this.tooltipVisible = false;
        }
    }
};
</script>

<style scoped>
circle {
    transition: all 0.2s ease-in-out;
    cursor: pointer;
}

circle:hover {
    opacity: 1;
    stroke-width: 3;
    filter: drop-shadow(0px 0px 8px rgba(0, 0, 0, 0.15));
}
</style>